import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { TourProgressAdapter, TourProgressRecord, TourProgressStatus } from '../types';

/** Lives under the session key, so signing out (which clears session queries) clears it too. */
export const tourProgressQueryKey = ['session', 'me', 'tours'] as const;

export interface UseTourProgressOptions {
  /** False until the shell has a session (and never while the two-factor gate is up). */
  enabled: boolean;
  /** Support sessions read but never write; the backend refuses the writes anyway. */
  readOnly: boolean;
}

export interface TourProgress {
  records: ReadonlyMap<string, TourProgressRecord>;
  isPending: boolean;
  isError: boolean;
  /** Fire and forget: a failed save never toasts, progress is a convenience. */
  save: (tourId: string, version: number, status: TourProgressStatus) => void;
  reset: (tourId?: string) => Promise<void>;
  readOnly: boolean;
}

/**
 * Per-person tour progress over `GET/PUT/DELETE /me/tours`. Loaded lazily (not in a route
 * loader) so it never holds up the first paint. Saves update the cache optimistically: a PUT of
 * the same status is idempotent (ADR 0053 allows it for such toggles).
 */
export function useTourProgress(
  adapter: TourProgressAdapter,
  { enabled, readOnly }: UseTourProgressOptions,
): TourProgress {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: tourProgressQueryKey,
    queryFn: () => adapter.list(),
    staleTime: 5 * 60_000,
    enabled,
    meta: { silentError: true },
  });

  const saveMutation = useMutation({
    mutationFn: ({
      tourId,
      version,
      status,
    }: {
      tourId: string;
      version: number;
      status: TourProgressStatus;
    }) => adapter.save(tourId, { version, status }),
    meta: { silentError: true },
    onMutate: ({ tourId, version, status }) => {
      const previous = queryClient.getQueryData<{ items: TourProgressRecord[] }>(
        tourProgressQueryKey,
      );
      const optimistic: TourProgressRecord = {
        tourId,
        version,
        status,
        updatedAt: new Date().toISOString(),
      };
      queryClient.setQueryData<{ items: TourProgressRecord[] }>(tourProgressQueryKey, {
        items: [...(previous?.items ?? []).filter((r) => r.tourId !== tourId), optimistic],
      });
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(tourProgressQueryKey, context.previous);
    },
  });

  const resetMutation = useMutation({
    mutationFn: (tourId?: string) => adapter.reset(tourId),
    meta: { silentError: true },
    onSettled: () => queryClient.invalidateQueries({ queryKey: tourProgressQueryKey }),
  });

  const items = query.data?.items;
  const records = useMemo(
    () => new Map((items ?? []).map((record) => [record.tourId, record])),
    [items],
  );

  return {
    records,
    isPending: enabled && query.isPending,
    isError: query.isError,
    readOnly,
    save: (tourId, version, status) => {
      if (readOnly || !enabled) return;
      saveMutation.mutate({ tourId, version, status });
    },
    reset: async (tourId) => {
      if (readOnly || !enabled) return;
      await resetMutation.mutateAsync(tourId);
    },
  };
}
